import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Sparkles, ArrowRight, ArrowLeft, Phone,
} from "lucide-react";
import MarketingLayout from "../components/MarketingLayout";
import PricingCalculator from "../components/PricingCalculator";
import usePublicSettings from "../lib/usePublicSettings";

/**
 * Dedicated pricing page (`/precos`) — extracted from the homepage so the
 * landing experience stays a clean conversion-focused teaser. Keeps the
 * "Dark Luxe" palette (deep green + gold) and re-uses the same card
 * markup the home Pricing section had before the refactor.
 */

const PHONE_LINK = "tel:+351936942709";

const TIERS = [
  {
    id: "essential",
    name: "Essential",
    price: "Desde 40€",
    desc: "Ideal para apartamentos e alojamentos locais que procuram um serviço de limpeza profissional, eficiente e preparado para check-in.",
    features: [
      "Limpeza completa da propriedade",
      "Preparação para hóspedes",
      "Verificação geral do alojamento",
      "Reposição básica de consumíveis",
      "Relatório pós-limpeza",
    ],
    cta: "Pedir Orçamento",
    featured: false,
  },
  {
    id: "professional",
    name: "Professional",
    price: "Desde 50€",
    desc: "Perfeito para proprietários que procuram um serviço mais detalhado e maior controlo de qualidade.",
    features: [
      "Limpeza premium",
      "Supervisão da propriedade",
      "Relatório detalhado",
      "Registo fotográfico",
      "Reposição completa de consumíveis",
      "Lavandaria opcional",
    ],
    cta: "Pedir Orçamento",
    featured: true,
    badge: "Mais Popular",
  },
  {
    id: "premium-villa",
    name: "Premium Villa",
    price: "Orçamento Personalizado",
    desc: "Serviço exclusivo para villas e propriedades de luxo.",
    features: [
      "Limpeza premium detalhada",
      "Supervisão completa",
      "Preparação de alta qualidade",
      "Relatórios profissionais",
      "Serviço personalizado conforme a propriedade",
    ],
    cta: "Contactar",
    featured: false,
  },
];

// Pricing for the extras section: each entry can be a plain string (no
// price shown) or an object with a `from` price (e.g. "desde 10€"). The
// renderer below shows the price in a discreet gold pill.
const EXTRAS = [
  { label: "Lavandaria", from: "10€" },
  { label: "Reposição de amenities", from: "5€" },
  "Limpezas profundas",
  "Check-in / Check-out",
  "Supervisão de propriedades",
  "Serviços personalizados",
  "Manutenção sob consulta",
];

export default function PricingPage() {
  // Scroll the page to the top on mount — important when arriving from the
  // homepage teaser CTA, the in-app menu, or a deep link.
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, []);
  const { settings } = usePublicSettings();

  return (
    <MarketingLayout>
      {/* Hero / page header */}
      <section className="relative overflow-hidden bg-[#041f1b] py-16 sm:py-24"
               data-testid="pricing-page-hero">
        <div aria-hidden
             className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.10),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/"
                data-testid="pricing-back-home"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#a0b2af] hover:text-[#d4af37] transition-colors">
            <ArrowLeft size={14} /> Voltar à Home
          </Link>

          <div className="mt-8 max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-[0.22em] text-[#d4af37]">
              Tabela de serviços
            </span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#fdfbf7]">
              Planos &amp; <span className="italic text-[#d4af37]">Preços</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#a0b2af] leading-relaxed">
              Soluções flexíveis para alojamentos locais, Airbnb e propriedades premium na Madeira.
              Escolha o plano que melhor se adapta à sua propriedade — pode mudar a qualquer momento.
            </p>
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="bg-[#041f1b] pb-20 sm:pb-28" data-testid="pricing-page-tiers">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TIERS.map(t => (
              <article
                key={t.id}
                className={`relative rounded-2xl p-7 sm:p-8 transition-all duration-300 ${
                  t.featured
                    ? "bg-gradient-to-b from-[#0a3a33] to-[#062c27] border-2 border-[#d4af37] shadow-[0_18px_60px_-15px_rgba(212,175,55,0.45)] md:-translate-y-3"
                    : "bg-[#062c27] border border-[#1a4640] hover:border-[#d4af37]/60 hover:shadow-lg hover:shadow-[#d4af37]/10 hover:-translate-y-0.5"
                }`}
                data-testid={`pricing-page-card-${t.id}`}
              >
                {t.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#d4af37] text-[#041f1b] text-[10px] uppercase tracking-[0.22em] font-bold whitespace-nowrap shadow-lg"
                       data-testid={`pricing-page-badge-${t.id}`}>
                    ★ {t.badge}
                  </div>
                )}
                <h3 className="font-display text-2xl text-[#fdfbf7]">{t.name}</h3>
                <div className="mt-4 mb-1 flex items-baseline gap-1">
                  <span className={`font-display text-2xl sm:text-3xl ${t.featured ? "text-[#d4af37]" : "text-[#fdfbf7]"} tabular-nums uppercase tracking-widest`}
                        data-testid={`pricing-page-price-${t.id}`}>
                    Sob consulta
                  </span>
                </div>
                <p className="mt-4 text-sm text-[#a0b2af] leading-relaxed min-h-[80px]">
                  {t.desc}
                </p>
                <ul className="mt-6 space-y-2.5">
                  {t.features.map(f => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-[#fdfbf7]/90">
                      <CheckCircle2 size={16} className="text-[#d4af37] shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/#contacto"
                      data-testid={`pricing-page-cta-${t.id}`}
                      className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold uppercase tracking-widest text-xs transition-all ${
                        t.featured
                          ? "bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40"
                          : "border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#041f1b]"
                      }`}>
                  {t.cta} <ArrowRight size={14} />
                </Link>
              </article>
            ))}
          </div>

          {/* Calculadora de preços */}
          {settings.pricing_calculator_enabled && (
            <div className="mt-16 sm:mt-20 max-w-5xl mx-auto" data-testid="pricing-calculator-wrap">
              <PricingCalculator />
            </div>
          )}

          {/* Serviços Adicionais */}
          <div className="mt-16 sm:mt-20 max-w-4xl mx-auto"
               data-testid="pricing-page-extras">
            <h2 className="font-display text-2xl sm:text-3xl text-center text-[#fdfbf7]">
              Serviços <span className="italic text-[#d4af37]">Adicionais</span>
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {EXTRAS.map(ex => {
                const label = typeof ex === "string" ? ex : ex.label;
                const from = typeof ex === "object" ? ex.from : null;
                return (
                  <li key={label}
                      className="flex items-center gap-2.5 px-4 py-3 bg-[#062c27] border border-[#1a4640] rounded-lg text-sm text-[#fdfbf7]/90 hover:border-[#d4af37]/40 transition-colors">
                    <Sparkles size={14} className="text-[#d4af37] shrink-0" />
                    <span className="flex-1">{label}</span>
                    {from && (
                      <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/30 shrink-0">
                        a partir de {from}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          <p className="mt-12 text-center text-xs sm:text-sm text-[#a0b2af] max-w-3xl mx-auto leading-relaxed italic"
             data-testid="pricing-page-disclaimer">
            Os valores podem variar conforme a dimensão da propriedade, localização e
            necessidades específicas do serviço.
          </p>

          {/* Bottom CTA strip */}
          <div className="mt-16 sm:mt-20 rounded-2xl border border-[#1a4640] bg-[#062c27] p-8 sm:p-10 text-center"
               data-testid="pricing-page-bottom-cta">
            <h3 className="font-display text-2xl sm:text-3xl text-[#fdfbf7]">
              Não sabe qual o plano certo?
            </h3>
            <p className="mt-3 text-sm sm:text-base text-[#a0b2af] max-w-xl mx-auto">
              Fale connosco em poucos minutos — preparamos uma proposta sob medida para a sua propriedade.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/#contacto"
                    data-testid="pricing-page-cta-contact"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-semibold uppercase tracking-widest text-xs bg-[#d4af37] text-[#041f1b] hover:bg-[#e8c860] hover:shadow-lg hover:shadow-[#d4af37]/40 transition-all">
                Pedir orçamento <ArrowRight size={14} />
              </Link>
              <a href={PHONE_LINK}
                 data-testid="pricing-page-cta-phone"
                 className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md font-semibold uppercase tracking-widest text-xs border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#041f1b] transition-all">
                <Phone size={14} /> Ligar agora
              </a>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
